import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck, FileText, ChevronDown } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  "Professional Care",
  "Right AT HOME",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden bg-brand-navy">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src="https://images.pexels.com/photos/7551622/pexels-photo-7551622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Caregiver providing compassionate home care to a senior"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-brand-navy/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/80 via-brand-navy/40 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex items-center gap-4"
        >
          <span className="h-px w-14 bg-brand-cyan" />
          <span className="text-sm md:text-base font-bold uppercase tracking-[0.3em] text-brand-cyan drop-shadow-[0_1px_4px_rgba(11,27,61,0.8)]">BMS Health Care Services</span>
        </motion.div>

        <h1 className="mt-8 font-display font-semibold text-white text-4xl leading-[1.1] sm:text-5xl lg:text-6xl tracking-tight max-w-4xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-slate-200"
        >
          Through compassionate, personalized care — delivered with dignity, respect, and excellence in the comfort of home.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-consultation-cta"
            className="inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-7 py-4 text-base md:text-lg font-bold text-white shadow-[0_12px_36px_rgba(0,128,128,0.45)] hover:bg-brand-cyan hover:text-brand-navy transition-colors duration-300 active:scale-95"
          >
            <CalendarCheck className="h-5 w-5" strokeWidth={1.8} />
            Book a Free Care Consultation
          </Link>
          <Link
            to="/referral"
            data-testid="hero-referral-cta"
            className="inline-flex items-center gap-2.5 rounded-full border border-white/50 px-7 py-4 text-base md:text-lg font-bold text-white hover:bg-white hover:text-brand-navy transition-colors duration-300 active:scale-95"
          >
            <FileText className="h-5 w-5" strokeWidth={1.8} />
            Refer a Patient
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 text-sm md:text-base font-semibold uppercase tracking-[0.22em] text-slate-200"
        >
          Surrey · Vancouver · White Rock · Langley · Delta · Richmond · Burnaby · New Westminster · Coquitlam
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ChevronDown className="h-6 w-6" strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
};
